<template>
  <config-base
    class="opc-zcm-config"
    @view-details="showPlanDetailDialog"
    :config-plan-tile-status="configPlanTileStatus"
  >
    <template #config>
      <div>
        <div class="opc-zcm-config__item-label">{{ $t('billing.config.select_number_users') }}</div>
        <license-tip
          v-if="enableCAICSwitchToStandalone"
          :type="planType"
          :number="licenseNumber"
          :additionalSpans="additionalSpans"
          @click="handleLicenseTipsClick"
        />
        <input-number-with-popover
          ref="userInput"
          size="lg"
          controls-position="both"
          class="gap8"
          v-model="licenseNumber"
          :min="minLicenses"
          :max="maxLicenses"
          :label="`${title} select the number of users`"
          :increaseTips="increaseTips"
          :decreaseTips="decreaseTips"
          :dataTracking="{ buyInteraction: 'number of users', planName: planTrackingName }"
          :data-testid="`checkout-input-license-${selectedPlan ? selectedPlan.plan_code : ''}`"
          @change="updatePlanLicense"
        />
        <zm-banner show-close v-if="showLicenseMaxAlert" type="info" class="gap16 custom-avatars-config__license-alert">
          <p v-html="$t('billing.custom_avatars.addon_user_limit_tips', [title, $t(currentBasePlan.planNameKey)])"></p>
        </zm-banner>
        <zm-banner
          show-close
          v-if="mx_downSellLicenseErrorMessage"
          type="info"
          class="gap16 opc-zcm-config__license-alert"
        >
          {{ mx_downSellLicenseErrorMessage }}
        </zm-banner>
        <config-cycle
          v-model="billingCycle"
          :show-cycle-alert="!basePlanInSelected"
          :showMonthlyPriceFromCycle="true"
          :billing-cycle-list="billingCycleList"
          @switchCycleAction="updateBillingCycle"
          :unit="unit"
        />
        <zm-banner
          v-if="enableCAICPlanRebranding"
          class="gap16"
          :content="$t('billing.zoom_custom_ai_companion.rebranding_notice')"
          type="info"
        />
        <preview-payment class="gap24" />
      </div>
    </template>
    <template #footer v-if="isShowRemoveBtn">
      <zm-button
        data-testid="checkout-button-93"
        type="link"
        ghost
        @click="removePlanByKey"
        :aria-label="`remove ${title} plan`"
      >
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script>
import { get, chain, includes } from 'lodash';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import ConfigBase from '@/components/config-base.vue';
import ConfigCycle from '@/components/service/config-billing-cycle/index.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover.vue';

import LicenseTip from '../ZOOM_MEETING/license-tip.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import { plansof } from '@/utils/chain';
import { translateToEn } from '@/plugins/i18n';
import { planUnitMap } from '@/common/product-unit';
import plansMixin from '../mx/plan';
import addonMixin from '../mx/addon';
import planDetailsDialogMixin from './components/plan-details/plan-details-dialog';
import commonMixin from './mixin';
import autofill from '@/plans/mx/autofill';
import { planTypes, ZOOM_CAIC_MAX_LICENSE, ZOOM_CAIC_MIN_LICENSE } from '@/common/constants';
import { enableCAICSwitchToStandalone, enableCAICPlanRebranding } from './utils.js';
import autoAddMixin, { showSyncAddonMessage } from '@/common/mixin/autoAdd';

const ConfigBaseComp = {
  name: 'ZoomCustomAiCompanionBase',
  extends: ConfigBase,
  mixins: [commonMixin],
  key: planTypes[planTypes.ZOOM_CUSTOM_AI_COMPANION],
  data() {
    return {
      showLearnMore: false,
      showPlanDetails: true,
      unit: planUnitMap.user,
    };
  },
};

export default {
  name: 'ZoomCustomAiCompanionConfig',
  mixins: [
    commonMixin,
    plansMixin,
    addonMixin,
    planDetailsDialogMixin,
    autofill,
    enableCAICSwitchToStandalone ? {} : autoAddMixin,
  ],
  components: { ConfigBase: ConfigBaseComp, ConfigCycle, InputNumberWithPopover, PreviewPayment, LicenseTip },
  data() {
    return {
      billingCycle: '',
      licenseNumber: 1,
      unit: planUnitMap.user,
      additionalSpans: {
        [planTypes.ZOOM_CUSTOM_AI_COMPANION]: [
          {
            value: ZOOM_CAIC_MIN_LICENSE,
            default: true,
            text: '',
          },
          {
            value: ZOOM_CAIC_MAX_LICENSE,
            text: this.$t('billing.zone.max_of_biz'),
          },
        ],
      },
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'getCurrentBasePlanInfo',
      'basePlanTotalLicenses',
      'getUserCurrentSubscriptions',
      'basicConfig',
      'currentEditPlan',
    ]),

    enableCAICSwitchToStandalone() {
      return enableCAICSwitchToStandalone;
    },

    enableCAICPlanRebranding() {
      return enableCAICPlanRebranding;
    },

    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },

    currentBasePlan() {
      return get(this.getCurrentBasePlanInfo, ['plans', 0], {});
    },

    previousQuantity() {
      return this.currentEditPlan?.cartInfo?.selected_number ?? this.selectedPlan.min;
    },

    disableDownSell() {
      // Check account MRR > 500?
      return this.basicConfig.downSellData.disableDownSell;
    },

    minLicenses() {
      if (this.disableDownSell || !this.isStandardPrice) {
        return this.previousQuantity;
      }
      return this.selectedPlan.min;
    },

    maxLicenses() {
      if (this.mx_canAutoAdd) {
        return this.mx_maxAutoAddLicense;
      }
      return enableCAICSwitchToStandalone ? this.selectedPlan.max : this.basePlanTotalLicenses;
    },

    /**
     * license number increase disabled tips
     * if base plan in selected plans show tips
     * else show 'Return to Plan Management' alert
     */
    increaseTips() {
      if (this.mx_canAutoAdd) {
        return '';
      }
      if (
        !enableCAICSwitchToStandalone &&
        this.basePlanInSelected &&
        this.licenseNumber >= this.basePlanTotalLicenses
      ) {
        return this.$t('billing.licences_limit_tips', [this.title, this.$t(this.currentBasePlan.planNameKey)]);
      }
      return '';
    },

    /**
     * license number decrease disabled tips
     */
    decreaseTips() {
      if (this.licenseNumber === this.previousQuantity) {
        if (this.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }
        if (this.forNonStandardPriceTips) {
          return this.forNonStandardPriceTips;
        }
      }
      return '';
    },

    /**
     * Show 'Return to Plan Management' alert
     * if base plan in selected plans, no need to show
     */
    showLicenseMaxAlert() {
      if (enableCAICSwitchToStandalone) return false;
      if (this.mx_canAutoAdd) {
        return false;
      }
      if (!this.basePlanInSelected && this.licenseNumber >= this.basePlanTotalLicenses) {
        return true;
      }
      return false;
    },

    /**
     * implementing within plansMixin
     * disable billing cycle
     */
    currentPlanCycleDepFirstPlan() {
      const dependCyclePlanList = get(this.selectedPlan, 'billingCycleDependBasePlanTypeList', []);

      return chain(this.getUserCurrentSubscriptions)
        .filter((p) => includes(dependCyclePlanList, p.type))
        .filter((p) => !p.cancelled)
        .orderBy((p) => p.cycleValue, 'desc')
        .get(0)
        .value();
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicense() {
      return (billingCycle) => {
        return (
          this.isEditFlow &&
          this.basicConfig?.isBulkLicenseAccount &&
          this.currentEditPlan?.billing_cycle !== billingCycle
        );
      };
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicenseTips() {
      return this.$t('billing.error.buld_acount_license');
    },

    /**
     * Is current base plan in selected plans
     */
    basePlanInSelected() {
      return this.getSelectedPlans.some((plan) => plan.type === this.currentBasePlan.type);
    },

    planTrackingName() {
      return translateToEn(this.selectedPlan?.planNameKey);
    },
  },
  watch: {
    maxLicenses(max) {
      if (this.licenseNumber > max) {
        this.licenseNumber = max;
        this.updatePlanLicense(max);
        showSyncAddonMessage(this.$t(this.selectedPlan.planNameKey), this.$t(this.currentBasePlan.planNameKey));
      }
    },
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),

    setCartLicense(license) {
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: license },
      });
    },
    /**
     * update license number
     * @param {number} newLicense
     */
    updatePlanLicense(newLicense) {
      this.setCartLicense(newLicense);
      this.mx_validDownSellLicense(newLicense);
      if (this.mx_canAutoAdd) {
        this.mx_handlePlanLicenseChange(newLicense).catch((previousLicense) => {
          this.licenseNumber = previousLicense;
          this.setCartLicense(previousLicense);
        });
      }
    },

    mx_onBasePlanLicenseChange(newLicense) {
      if (this.licenseNumber > newLicense) {
        this.licenseNumber = newLicense;
        this.setCartLicense(newLicense);
        this.mx_showSyncAddonMessage();
      }
    },

    /**
     * update billing cycle
     * @param {object} oldPlan
     * @param {object} newPlan
     */
    async updateBillingCycle(oldPlan, newPlan) {
      this.$tracking.buyInteraction('subscription cycle', `${this.planTrackingName} - ${newPlan.billing_cycle}`);

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        { ...newPlan, cartInfo: { selected_number: this.licenseNumber } },
      ]);
    },

    handleLicenseTipsClick(e) {
      if (e?.target?.tagName === 'A') {
        this.$tracking.buyInteraction(
          `banner - product level`,
          `${this.planTrackingName} - the maximum amount is ${ZOOM_CAIC_MAX_LICENSE} licenses - contact sales`,
        );
      }
    },
  },

  created() {
    if (this.selectedPlan) {
      this.billingCycle = this.selectedPlan.billing_cycle;
      this.licenseNumber = this.selectedPlan.cartInfo?.selected_number;

      // refresh page
      this.$nextTick(() => {
        this.mx_validDownSellLicense(this.licenseNumber);
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
@import '@/styles/zoom-card.scss';

$sub-font-color: var(--zoom-color-text-stronger-neutral, #2a2b2d);
$main-font-color: var(--zoom-color-text-stronger-neutral, #2a2b2d);

:deep(.gap8) {
  margin-top: 8px;
}
.gap16 {
  margin-top: 16px;
}
.gap24 {
  margin-top: 24px;
}

.opc-zcm-config {
  :deep .opc-zm-base-card {
    &__name {
      text-transform: none;
    }
    &__main--desc__content {
      font-size: 16px;
      color: $sub-font-color;
    }
  }

  &__item-label {
    font-size: 16px;
    font-weight: 600;
    color: $main-font-color;
    line-height: 20px;
  }

  &__license-alert {
    padding: 16px;
    color: $sub-font-color;
    font-size: 14px;
    line-height: 21px;
    a {
      font-weight: 450;
    }
  }
}
</style>
